import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, User, UserPlus } from "lucide-react";

/**
 * Member picker — dropdown of `company.members` + "Other / External Party"
 * option for inter-company / new-shareholder transfers.
 *
 * Optionally returns the picked member's folio_no + shares_held + class via
 * `onPickMember` so callers can pre-fill adjacent fields (transferor_folio,
 * share_class, etc.).
 *
 * Props:
 *   - members: array of {name, folio_no, shares_held, member_type, ...}
 *   - value: current name string
 *   - onChange: (newName: string) => void
 *   - onPickMember?: (member: {...} | null) => void — fires when user picks
 *                     a roster row or chooses "Other"
 *   - placeholder?: string
 *   - testId?: string  e.g. "specific-0-transferor_name"
 *   - allowFreeText?: boolean — when true (default), user can type a new name
 *                     (acts like a combobox); when false, force-select from list.
 */
export default function MemberPicker({
  members = [],
  value = "",
  onChange,
  onPickMember,
  placeholder = "Pick a member or type a new name…",
  testId = "member-picker",
  allowFreeText = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.folio_no || "").toLowerCase().includes(q),
    );
  }, [members, query]);

  const pick = (m) => {
    onChange(m.name || "");
    onPickMember?.(m);
    setOpen(false);
    setQuery("");
  };

  const pickOther = () => {
    // Don't clear `value` — let the user type. Notify upstream that no
    // roster match exists so adjacent prefills get cleared.
    onPickMember?.(null);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (allowFreeText) onPickMember?.(null);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full border border-zinc-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
          data-testid={testId}
          readOnly={!allowFreeText}
        />
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="border border-zinc-300 border-l-0 px-2 bg-white text-zinc-500 hover:text-[#002FA7]"
          data-testid={`${testId}-toggle`}
          aria-label="Open member list"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-30 mt-1 w-full bg-white border border-zinc-300 shadow-lg max-h-64 overflow-auto"
          data-testid={`${testId}-popover`}
        >
          <div className="sticky top-0 bg-white border-b border-zinc-200 p-2 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-zinc-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or folio…"
              className="flex-1 text-sm focus:outline-none"
              data-testid={`${testId}-search`}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500 text-center">
              No matching member in the company roster.
            </div>
          ) : (
            filtered.map((m, i) => (
              <button
                key={`${m.folio_no || m.name || i}`}
                type="button"
                onClick={() => pick(m)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#002FA7]/5 border-b border-zinc-100 flex items-center gap-2"
                data-testid={`${testId}-option-${i}`}
              >
                <User className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-900 truncate">{m.name || "(unnamed)"}</div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    Folio {m.folio_no || "—"} · {m.shares_held || 0} {m.member_type || "shares"}
                  </div>
                </div>
              </button>
            ))
          )}
          {allowFreeText && (
            <button
              type="button"
              onClick={pickOther}
              className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 border-t border-amber-200 bg-amber-50/50 flex items-center gap-2"
              data-testid={`${testId}-option-other`}
            >
              <UserPlus className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-amber-800">Other / External party — type the name above</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
